import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '../firebase';

// Everything around a bug report that might help triage it (Matt, 2026-10-10:
// "make sure that we are always passing in any information that might or even
// remotely could be relevant to any bug report"). The folded game view rides
// separately as gameState; this is the rest - where the reporter is, who they
// are at the table, the room, the connection, recent errors, the build.
//
// Compact on purpose (no maps, no placements blob - the view already carries
// the maps) and never anyone's email or a token. Every piece is gathered
// behind its own try/catch: a snapshot that fails must never stop the report.

const MAX_ERRORS = 8;
const recentErrors = [];
let connection = { firebaseConnected: null, changedAt: null };
let installed = false;

const pushError = (kind, message) => {
  recentErrors.push({
    kind,
    message: String(message || '').slice(0, 300),
    at: new Date().toISOString(),
  });
  if (recentErrors.length > MAX_ERRORS) recentErrors.shift();
};

// Components that hold UI state the store doesn't (the selected shape, an
// open modal) register a small getter here while they're mounted.
const uiProviders = new Map();
export const registerBugUi = (name, fn) => {
  uiProviders.set(name, fn);
  return () => { if (uiProviders.get(name) === fn) uiProviders.delete(name); };
};

/** Called once at startup: start listening for errors and the RTDB link. */
export function installBugContext() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    pushError('error', e?.message || e?.error?.message);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    pushError('unhandledrejection', r?.message || (typeof r === 'string' ? r : String(r)));
  });
  try {
    if (db) {
      onValue(dbRef(db, '.info/connected'), (snap) => {
        connection = { firebaseConnected: Boolean(snap.val()), changedAt: new Date().toISOString() };
      });
    }
  } catch { /* no connection tracking; the report still goes */ }
}

export const recordBugError = (kind, message) => pushError(kind, message);

const safe = (fn) => {
  try { return fn(); } catch (e) { return { snapshotError: String(e?.message || e).slice(0, 200) }; }
};

const screenOf = (state, getters) => {
  if (!getters.isApproved) return 'gate';
  if (getters.view) return 'game';
  if (state.roomId) return 'lobby';
  return 'menu';
};

export function gatherBugContext(store) {
  const { state, getters } = store || {};
  const ctx = {};

  ctx.app = safe(() => ({
    version: process.env.VUE_APP_GIT_SHA || 'unknown',
    buildTime: process.env.VUE_APP_BUILD_TIME || null,
    standalone: Boolean(window.matchMedia?.('(display-mode: standalone)').matches
      || window.navigator.standalone),
    serviceWorker: Boolean(navigator.serviceWorker?.controller),
  }));

  ctx.device = safe(() => ({
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    visualViewport: window.visualViewport
      ? `${Math.round(window.visualViewport.width)}x${Math.round(window.visualViewport.height)}`
      : null,
    orientation: window.screen?.orientation?.type || null,
    language: navigator.language || null,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    visibility: document.visibilityState,
  }));

  ctx.network = safe(() => ({
    navigatorOnline: navigator.onLine,
    ...connection,
  }));

  if (!state) return ctx;

  ctx.screen = safe(() => screenOf(state, getters));

  ctx.auth = safe(() => ({
    signedIn: Boolean(state.user),
    uid: state.user?.uid || null,
    displayName: state.user?.displayName || null,
    authReady: state.authReady,
    siteUserReady: state.siteUserReady,
    siteStatus: state.siteUser?.status || null,
    isAdmin: Boolean(getters.isAdmin),
  }));

  ctx.settings = safe(() => ({ ...state.settings }));

  ctx.mode = safe(() => (state.roomId ? 'online' : (state.local ? 'local' : 'none')));

  ctx.room = safe(() => {
    if (!state.roomId) return null;
    const meta = state.roomMeta || {};
    return {
      roomId: state.roomId,
      roomCode: meta.roomCode || null,
      status: meta.status || null,
      async: Boolean(meta.async),
      isHost: Boolean(getters.isHost),
      seatCount: (meta.playerSlots || []).length,
      botCount: (meta.playerSlots || []).filter((s) => s.isBot).length,
      presentUids: Object.keys(state.presence || {}).filter((k) => state.presence[k]).length,
      botDriverIsMe: getters.myDriverUid === state.user?.uid,
      hasGameNode: Boolean(state.roomGame),
      roomError: state.roomError ? String(state.roomError).slice(0, 300) : null,
    };
  });

  ctx.game = safe(() => {
    const view = getters.view;
    const players = getters.players || [];
    const base = {
      hasSavedGame: Boolean(state.hasSavedGame),
      seed: getters.seed ?? null,
      players: players.map((p) => ({ seat: p.seat, name: p.name || null, isBot: Boolean(p.isBot) })),
      mySeat: getters.mySeat ?? null,
    };
    if (!view) return base;
    const turn = view.turn;
    const placedThisTurn = Object.keys(getters.placements?.[view.turnIndex] || {}).length;
    return {
      ...base,
      phase: view.phase,
      turnIndex: view.turnIndex,
      totalTurns: view.script?.turns?.length ?? null,
      season: turn ? turn.season : null,
      turn: turn ? {
        type: turn.type,
        cardId: turn.cardId,
        ruins: Boolean(turn.ruins),
        direction: turn.direction || null,
      } : null,
      myMapSeat: getters.myMapSeat ?? null,
      iHavePlaced: Boolean(getters.iHavePlaced),
      placedThisTurn,
      stillDrawing: (getters.stillDrawing || []).map((p) => p.name || p.id),
      totals: view.totals,
      seasonTotals: (view.seasonScores || []).map((s) => s.map((m) => m.total)),
      coins: (view.maps || []).map((m) => m.coins),
    };
  });

  ctx.ui = safe(() => {
    const out = {};
    uiProviders.forEach((fn, name) => { out[name] = safe(fn); });
    return out;
  });

  ctx.recentErrors = recentErrors.slice();
  return ctx;
}
